import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePrevious } from 'react-use';

import { BrandType } from '@skytvnz/sky-app-store/lib/types/enums/BrandType';
import { AppId, Season } from '@skytvnz/sky-app-store/lib/types/graph-ql';

import { Video } from '@/Analytics';
import { VideoContentEventProps, VideoEventType } from '@/Analytics/Video';
import usePersistCallback from '@/Hooks/usePersistCallback';
import { actions, selectors, utils } from '@/Store';

import useIsLoaded from '@/Hooks/useIsLoaded';
import { getDeviceId } from '@/Utils/Device';
import { convertDurationToSeconds } from '@/Utils/DurationFormatter';
import sessionId from '@/Utils/SessionId';
import { isEmpty, isNil } from 'ramda';
import { generateNonce } from '@/Utils/InitPal';
import { ContentPayloadProps, VideoContentPayloadProps } from '@/Analytics/SsaiTracking';
import { isoTimeToSeconds } from '@/Utils/TimeFormat';
import { getUnifiedSessionId } from '@/Providers/BrowserSessionProvider';
import { DRM_TYPE, getEncodingType } from '../DRM';
import useParentalForbidden from '../ParentalControlPanel/useParentalForbidden';

const useVodData = (
  isDASH: boolean,
  brandId: string,
  episodeId?: string,
  isShow?: boolean,
  isParentalRestrictionResolved?: boolean,
  playbackOrigin?: string,
) => {
  const dispatch = useDispatch();
  const selectedProfile = useSelector(selectors.customer.selectedProfile);
  const brand = useSelector(selectors.titles.getBrand)(brandId);
  const brandError = useSelector(selectors.titles.brandError);
  const isBrandLoading = useSelector(selectors.titles.brandIsLoading);
  const isBrandLoaded = useIsLoaded(isBrandLoading);
  const getShowEpisode = useSelector(selectors.titles.getShowEpisode);
  const episode = episodeId ? getShowEpisode(brandId, episodeId) : null;

  const selectedSeason = useSelector(selectors.titles.getSelectedSeason)(brandId);
  const seasons = useSelector(selectors.titles.getSeasons)(brandId);

  const playbackPosition =
    brand?.__typename === BrandType.Movie ? brand.watchProgress : episode?.watchProgress;

  const mediaAsset = brand?.__typename === BrandType.Movie ? brand.asset : episode?.asset;
  const mediaAssetId = mediaAsset?.id;

  const playbackMeta = useSelector(selectors.playback.getPlaybackMeta)(mediaAssetId || '');
  const playbackMetaError = useSelector(selectors.playback.playbackMetaError);
  const isPlaybackError = brandError || playbackMetaError;

  const isConcurrencyLimitReached = useSelector(selectors.playback.isConcurrentPlaybackError)(
    mediaAssetId || '',
  );

  const contentPayload: ContentPayloadProps = {
    id: isShow ? episode?.id : brand?.id,
    type: brand?.__typename,
    duration: isoTimeToSeconds(brand?.duration ?? episode?.duration),
  };

  const previousEpisode = usePrevious(episode);

  const adParams = givN => {
    const params = { sid: sessionId, givn: givN };
    if (isNil(givN) || isEmpty(givN)) {
      // if givN is null or undefined, do not add to params
      delete params.givn;
    }
    return params;
  };

  // Parental forbidden flag
  const { isParentalForbidden, isParentalForbiddenRef } = useParentalForbidden(
    mediaAsset,
    isParentalRestrictionResolved,
  );

  const captureVideoContentEvent = useCallback(
    (
      eventType: VideoEventType,
      position: number,
      origin?: string,
      watchDuration?: number,
      adPayload: VideoContentPayloadProps = {},
    ) => {
      if (playbackMeta && brand) {
        const eventProps: VideoContentEventProps = {
          title: utils.analytics.getSegmentVodTitle(brand, episode) ?? '',
          genre: utils.analytics.getSegmentBrandGenres(brand),
          type: utils.analytics.getContentTypeByBrand(brand),
          origin: origin || playbackOrigin || '',
          platform: 'Web',
          playback: 'VOD',
          position: Math.floor(position),
          duration: convertDurationToSeconds(mediaAsset?.duration),
          channel: utils.analytics.getSegmentControllingChannel(brand),
          content_id: isShow ? episode?.id : brand?.id,
          season: utils.analytics.getVodSeasonNumber(selectedSeason),
          episode: utils.analytics.getVodEpisodeNumber(episode, utils.title.isSport(brand)),
          watch_duration: watchDuration,
          session_id: getUnifiedSessionId() ?? sessionId,
        };

        Video.recordContentEvent(eventType, eventProps, adPayload);
      }
    },
    [brand, isShow, episode, playbackMeta, playbackOrigin, selectedSeason, mediaAsset],
  );

  const selectSeason = usePersistCallback((season: Season) => {
    dispatch(actions.titles.selectSeason(brandId, season.id));
  });

  const clearVodPlayback = usePersistCallback(async (assetId, shouldStop = true) => {
    if (!assetId) {
      return;
    }
    if (shouldStop) {
      await dispatch(actions.playback.stopPlayback(assetId, false, getDeviceId()));
    }
    dispatch(actions.playback.removePlaybackMeta(assetId));
  });

  const stopPreviousEpisodePlayback = usePersistCallback(async () => {
    const previousAssetId = previousEpisode?.asset?.id;
    if (previousAssetId && previousAssetId !== mediaAssetId) {
      await dispatch(actions.playback.stopPlayback(previousAssetId, false, getDeviceId()));
    }
  });

  const onPlaybackRetry = usePersistCallback(() => {
    if (mediaAssetId) {
      dispatch(
        actions.playback.fetchPlaybackMeta(
          mediaAssetId,
          false,
          DRM_TYPE,
          getDeviceId(),
          getEncodingType(),
        ),
      );
    }
  });

  useEffect(() => {
    if (!brandId || isBrandLoaded) {
      return;
    }
    dispatch(actions.titles.fetchBrand({ brandId, isShow }));
  }, [dispatch, brandId, isBrandLoaded, isShow]);

  useEffect(() => {
    const fetchPlaybackData = async () => {
      if (isParentalForbidden === false && mediaAssetId) {
        await stopPreviousEpisodePlayback();
        const givN = await generateNonce(isDASH, selectedProfile?.ppid, sessionId);
        if (isParentalForbiddenRef.current) {
          // parental restriction changes after mediaAssetId fires, but the async
          // logic above completes after parental restriction changes so we can't clear
          // meta on restriction change. Need to clear meta to avoid
          // race condition in Safari where meta gets set twice close back to back and
          // player fails.
          dispatch(actions.playback.removePlaybackMeta(mediaAssetId));
          return;
        }
        dispatch(
          actions.playback.fetchPlaybackMeta(
            mediaAssetId,
            false,
            DRM_TYPE,
            getDeviceId(),
            getEncodingType(),
            AppId.SkyGoWeb,
            adParams(givN),
          ),
        );
      }
    };
    fetchPlaybackData();
  }, [
    dispatch,
    isParentalForbidden,
    isParentalForbiddenRef,
    mediaAssetId,
    stopPreviousEpisodePlayback,
    selectedProfile,
    isDASH,
  ]);

  return {
    brand,
    episode,
    seasons,
    selectedSeason,
    mediaAssetId,
    playbackMeta,
    isConcurrencyLimitReached,
    isPlaybackError,
    playbackPosition,
    isParentalForbidden,
    clearVodPlayback,
    onPlaybackRetry,
    selectSeason,
    captureVideoContentEvent,
    contentPayload,
  };
};

export default useVodData;
